import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import {
  monitoringByAgeData, deviceUsageData, consumablesData, pharmacyMonthlyPerformance,
  getDeviceModelById, getPharmacyById
} from "@/data/platformData";
import { servicesStats } from "@/data/mockData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from "recharts";
import {
  BarChart3, TrendingUp, Users, Activity, Cpu, Package, Euro, ArrowLeft
} from "lucide-react";

const PHARMACY_ID = "ph1";
const COLORS = ["hsl(var(--primary))", "hsl(var(--info))", "hsl(var(--success))", "hsl(var(--warning))", "hsl(var(--secondary))"];

export const ManagerMonitoringPage = () => {
  const navigate = useNavigate();
  const pharmacy = getPharmacyById(PHARMACY_ID);
  const ageData = monitoringByAgeData.filter(a => a.pharmacyId === PHARMACY_ID);
  const usage = deviceUsageData.filter(d => d.pharmacyId === PHARMACY_ID);
  const stock = consumablesData.filter(c => c.pharmacyId === PHARMACY_ID);
  const monthly = pharmacyMonthlyPerformance.filter(m => m.pharmacyId === PHARMACY_ID);

  const totalProcedures = servicesStats.reduce((a, b) => a + b.count, 0);
  const totalRevenue = monthly.reduce((a, b) => a + b.fatturato, 0);
  const lowStockCount = stock.filter(s => s.stock < s.threshold).length;
  const avgUptime = (usage.reduce((a, b) => a + b.uptime, 0) / usage.length).toFixed(1);

  const categoryData = Object.entries(servicesStats.reduce((acc, s) => {
    acc[s.category] = (acc[s.category] || 0) + s.count;
    return acc;
  }, {})).map(([name, count]) => ({ name, count }));

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/manager")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="h-4 w-4 text-indigo-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Business Intelligence</span>
          <MDRBadge variant="admin" size="sm" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Monitoraggio attività</h1>
        <p className="text-sm text-muted-foreground mt-1">{pharmacy?.name} · KPI per età, prestazioni, dispositivi e scorte</p>
      </div>

      {/* KPI strip */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Prestazioni totali", value: totalProcedures, icon: Activity, accent: "text-primary bg-primary/10" },
          { label: "Fatturato 6 mesi", value: `€ ${totalRevenue.toLocaleString("it-IT")}`, icon: Euro, accent: "text-emerald-700 bg-emerald-100" },
          { label: "Uptime device medio", value: `${avgUptime}%`, icon: Cpu, accent: "text-sky-700 bg-sky-100" },
          { label: "Scorte sotto soglia", value: lowStockCount, icon: Package, accent: "text-rose-700 bg-rose-100" }
        ].map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${k.accent}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold leading-none">{k.value}</p>
                  <p className="text-xs mt-1 text-muted-foreground">{k.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="ages" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="ages" data-testid="monitoring-tab-ages">Fasce d'età</TabsTrigger>
          <TabsTrigger value="services" data-testid="monitoring-tab-services">Prestazioni</TabsTrigger>
          <TabsTrigger value="devices" data-testid="monitoring-tab-devices">Dispositivi</TabsTrigger>
          <TabsTrigger value="stock" data-testid="monitoring-tab-stock">Consumabili</TabsTrigger>
          <TabsTrigger value="trends" data-testid="monitoring-tab-trends">Trend</TabsTrigger>
        </TabsList>

        <TabsContent value="ages" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-primary" /> Pazienti per età
                </CardTitle>
                <CardDescription>Distribuzione anagrafica utenza</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ageData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="range" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Pazienti" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quote percentuali</CardTitle>
                <CardDescription>Composizione utenza in %</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={ageData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="percentage" nameKey="range"
                        label={({ range, percentage }) => `${range} ${percentage}%`} labelLine={false}>
                        {ageData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="services" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top prestazioni</CardTitle>
                <CardDescription>Eseguite negli ultimi 6 mesi</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={servicesStats.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                      <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} width={110} />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Categorie</CardTitle>
                <CardDescription>Distribuzione prestazioni</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="count" nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Cpu className="h-4 w-4 text-sky-600" /> Utilizzo dispositivi medicali
              </CardTitle>
              <CardDescription>Esami eseguiti, ultimo utilizzo, uptime</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {usage.map(u => {
                  const m = getDeviceModelById(u.deviceModelId);
                  if (!m) return null;
                  return (
                    <div key={u.deviceModelId} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.type} · ultimo uso {u.lastUse}</p>
                      </div>
                      <div className="flex items-center gap-3 text-right shrink-0">
                        <div>
                          <p className="text-xs text-muted-foreground">Esami</p>
                          <p className="font-semibold">{u.uses}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Uptime</p>
                          <Badge variant="outline" className={u.uptime >= 99 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                            {u.uptime}%
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stock" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4 text-rose-600" /> Consumabili
              </CardTitle>
              <CardDescription>Giacenza vs soglia minima</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stock.map(s => {
                  const isLow = s.stock < s.threshold;
                  const ratio = Math.min(100, Math.round((s.stock / (s.threshold * 2)) * 100));
                  return (
                    <div key={s.id} className="p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm">{s.name}</p>
                        <Badge variant="outline" className={isLow ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                          {s.stock} {s.unit}
                        </Badge>
                      </div>
                      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${isLow ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${ratio}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Soglia {s.threshold} · Ultimo riordino {s.lastRestock}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" /> Trend ultimi 6 mesi
              </CardTitle>
              <CardDescription>Prestazioni, pazienti e fatturato</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthly}>
                    <defs>
                      <linearGradient id="cP" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="cF" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Legend />
                    <Area type="monotone" dataKey="prestazioni" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#cP)" name="Prestazioni" />
                    <Area type="monotone" dataKey="fatturato" stroke="#10b981" strokeWidth={2} fill="url(#cF)" name="Fatturato (€)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagerMonitoringPage;
